<template>
  <div class="d-flex align-center justify-center fill-height">
    <v-card style="width: 90vw; height: 90vh;" class="d-flex flex-column">
      <v-toolbar color="primary" max-height="64">
        <v-toolbar-title class="mx-5 altfont" v-if="model && !isSmallScreen">{{ model.name }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          v-if="isAdmin && !isResourcePublished"
          icon
          variant="plain"
          color="warning"
          v-tooltip.bottom-center="$t('cortona.ipc.resource-not-published', { status: props.model && props.model.status })"
          class="d-print-none"
        >
          <v-icon>mdi-alert-outline</v-icon>
        </v-btn>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="flex-grow-1 overflow-auto pa-0">
        <div v-if="loading" class="d-flex align-center justify-center fill-height">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <v-alert v-else-if="error" type="error" class="ma-4">{{ $t('resource-preview.text.error') }}</v-alert>

        <!-- CSV: render as table -->
        <v-table v-else-if="isCsv && csvRows.length" fixed-header height="100%">
          <thead>
            <tr>
              <th v-for="(header, i) in csvRows[0]" :key="i">{{ header }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, ri) in csvRows.slice(1)" :key="ri">
              <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
            </tr>
          </tbody>
        </v-table>

        <!-- Plain text -->
        <pre v-else class="pa-4 text-body-2" style="white-space: pre-wrap; word-break: break-word;">{{ textContent }}</pre>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import { useAccountStore } from '@/stores/account';

const props = defineProps<{
  model?: any;
}>();

const emit = defineEmits<{
  closeDialog: [];
}>();

const { width } = useDisplay();
const accountStore = useAccountStore();

const isSmallScreen = computed(() => width.value < 768);
const isAdmin = computed(() => accountStore.account?.userType === 'ADMIN');
const isResourcePublished = computed(() => !props.model || props.model.status === 'PUBLISHED');

const loading = ref(true);
const error = ref(false);
const textContent = ref('');
const csvRows = ref<string[][]>([]);

const isCsv = computed(() => props.model?.mimeType === 'text/csv');

const closeDialog = () => emit('closeDialog');

function parseCsv(text: string): string[][] {
  return text
    .split('\n')
    .filter(line => line.trim().length > 0)
    .map(line => line.split(',').map(cell => cell.trim().replace(/^"|"$/g, '')));
}

onMounted(async () => {
  if (!props.model?.path) {
    error.value = true;
    loading.value = false;
    return;
  }

  try {
    const headers: Record<string, string> = {};
    const accountStore = useAccountStore();
    if (accountStore.logon) {
      const token = localStorage.getItem('accessToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(props.model.path, { headers });
    if (!response.ok) throw new Error('fetch failed');

    const text = await response.text();
    textContent.value = text;
    if (isCsv.value) {
      csvRows.value = parseCsv(text);
    }
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<style lang="scss" scoped>
.altfont {
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: 1.5rem !important;
}
</style>
